import { Inject, Injectable, Injector, LOCALE_ID, Optional } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { browserPopupRedirectResolver, signInWithPopup } from 'firebase/auth';
import firebase from 'firebase/compat/app';
import { filter, firstValueFrom, map, Observable, of, Subject, Subscription, timeout } from 'rxjs';
import { NGXLogger as LoggerService } from "ngx-logger";
import { TranslationGeneralService } from '../shared/translation/translation.general.service';
import { UserService } from './user.service';
import { DbUser } from 'functions-lib';
import { EssErrorService, LogUserContextService } from 'ngx-essentia';
import { clearAuthState, writeAuthState } from '../shared/services/auth-state-storage';
import { readClaimsRefreshedAt, writeClaimsRefreshedAt } from '../shared/services/claims-refresh-storage';
import { AuthErrorService } from './auth-error.service';
import { isNetworkRequestFailed } from '../whoo/core/poll-errors';
import { UIMesageService } from '../shared/services/ui-message.service';
import { AccessGrantService, AdoptResult } from '../whoo/core/access-grant.service';
import { GoogleAnalyticsService } from '../shared/services/google-analytics.service';
import { AuthCodeApiService, AuthCodeOutcome } from './auth-code/auth-code-api.service';
import { warnToServer } from '../shared/services/server-warn';
import { ServerTimeService } from '../shared/services/server-time.service';
import { AuthDropContext, authStorageProbe, ProbeSummary } from './auth-storage-probe';

/** the provider ids of the social sign-ins - a `password` provider is not one, a code account has none */
const SOCIAL_PROVIDER_IDS = ['google.com', 'apple.com', 'facebook.com', 'github.com', 'microsoft.com'];

/** outcome of AuthService.waitForClaims */
export interface ClaimsWaitResult {
    /** the awaited delegation is in the id token */
    granted: boolean;
    /**
     * set when the functions reported a FAILED claims write (`users/{uid}/claimsError`), e.g.
     * `auth/claims-too-large`. Waiting longer would not help - the caller must say so.
     */
    errorCode?: string;
}

/** marker of the auth drop report in /uilogging (workorder-poll-init-auth-drop-hotfix-2026-10-29, task 2) */
export const AUTH_DROP_MESSAGE = 'auth state dropped after sign-in';
/**
 * marker of the storage anomaly line (workorder-auth-drop-ios-diagnostics-2026-11-03, task 2): the probe saw a null read of the
 * stored user or a storage event that removed it - once per session, also when no drop follows (the base rate)
 */
export const AUTH_STORAGE_ANOMALY_MESSAGE = 'auth storage anomaly';
/** how long the first drop waits for the user to come back before it is reported as `returned: false` */
const AUTH_DROP_REPORT_WAIT_MS = 10000;
/** a first drop within this time after the first sign-in of the app run counts as authDropOnStartup */
const AUTH_DROP_ON_STARTUP_MS = 5000;
/** waitForSignedInUser: how long to wait for the SDK-internal currentUser after authState emitted a user */
const SDK_USER_POLL_ATTEMPTS = 10;
const SDK_USER_POLL_MS = 100;
/** forceTokenRefreshOnce: at most one forced refresh per minute */
const TOKEN_REFRESH_MIN_INTERVAL_MS = 60000;

/** Firebase codes for an in-app browser or blocked web storage - see AuthErrorService */
const UNSUPPORTED_ENVIRONMENT_CODES = ['auth/operation-not-supported-in-this-environment', 'auth/web-storage-unsupported'];

/** where the Auth SDK keeps the signed-in user - see AuthService.authPersistenceType */
export type AuthPersistenceType = 'idb' | 'local' | 'session' | 'none' | 'unknown';
/** the key prefix of the stored user in localStorage / sessionStorage (`firebase:authUser:<apiKey>:<appName>`) */
const AUTH_USER_KEY_PREFIX = 'firebase:authUser:';

/** true when the storage holds a `firebase:authUser:` key - throws when the storage is blocked */
function storageHasAuthUser(storage: Storage): boolean {
    for (let index = 0; index < storage.length; index++) {
        if (storage.key(index)?.startsWith(AUTH_USER_KEY_PREFIX)) {
            return true;
        }
    }
    return false;
}

/**
 * The persistence of a signed-in user, read from the storage keys (workorder-auth-persistence-local-first-2026-10-31,
 * task 2): `local` / `session` when the key is there, otherwise `idb`; `unknown` when a storage access throws.
 */
export function detectAuthPersistence(): AuthPersistenceType {
    try {
        if (storageHasAuthUser(window.localStorage)) {
            return 'local';
        }
        if (storageHasAuthUser(window.sessionStorage)) {
            return 'session';
        }
        return 'idb';
    } catch {
        return 'unknown';
    }
}

/**
 * `stsTokenManager.expirationTime` of a compat user (its serialized form), null when not readable - remembered per emission
 * so that the drop report can say how long the token of the dropped user was still valid (tokenExpiresInMsAtDrop).
 */
function readTokenExpirationTime(user: firebase.User | null): number | null {
    try {
        const expirationTime = (user as unknown as { toJSON?: () => { stsTokenManager?: { expirationTime?: unknown } } } | null)?.toJSON?.()?.stsTokenManager?.expirationTime;
        return typeof expirationTime === 'number' ? expirationTime : null;
    } catch {
        return null;
    }
}

export enum AuthProvider {
    ALL = 'all',
    EmailAndPassword = 'firebase',
    Google = 'google',
    Apple = 'apple',
    Facebook = 'facebook',
    Twitter = 'twitter',
    Github = 'github',
    Microsoft = 'microsoft',
    Yahoo = 'yahoo',
    PhoneNumber = 'phoneNumber',
}

/**
 * Provides all services related to authentication with Firebase
 * Makes also sure that we subscribe to additional user information from the service DatabaseUserService
 * subscribeAuthAndDbUser()  should be called once when starting the app, We do that via the constructor
 * (there are other ways to do so but this is easy and seems to work)
 * subscribeAuthAndDbUser makes sure we get the latest data in afAuthUser (=== this.afAuth.auth.currentUser)
 * afAuthUser$  can be used to subscribe changes on afAuthUser for example to updates reactive form fields
 * inspirey by NgxAuthFirebaseUI
 *
 * Claims are set via backend : setDelegationCustomClaim. The function stamps `users/{uid}/claimsUpdatedAt`
 * afterwards; onDbUserChanged sees the stamp on the dbUser subscription and refreshes the id token once.
 * Since workorder 1.18 the merge paths (anonymous session signs in to an existing account) no longer create
 * a delegation: the session's polls, answers and held grants are collected as the anonymous user, parked
 * in sessionStorage (`adoptGrants`) across the sign-in and written as grants under the target uid
 * (AccessGrantService.collectSessionItems / applyStashedGrants). The claims machinery stays for the legacy
 * account link (`assign-access`) until its retirement (1.17).
 * Since code auth P2 (2026-10-24) sign-up and the upgrade of an anonymous session run per e-mail code
 * (signInWithAuthCode: custom token from `/auth-code`, no password, no verification mail); the password paths
 * signUpEmail / upgradeAnonymousWithEmail / mergeWithPasswordAccount are gone. Since P3 the login runs per code
 * as well; the password login (signInWithEmail) stays as the collapsed second way behind the RTDB flag
 * `/config/auth/passwordLoginEnabled` (AuthConfigService) until P4, the password reset and the verification mail
 * are gone (the legacy gate in the login form verifies an unconfirmed password account per code instead).
 */

@Injectable({
    providedIn: 'root', // specifies that Angular should provide the service in the root injector - ?? I gues we need that to init the the service in app.module
})
export class AuthService {
    public isLoading = false;
    private authUserSubscription: Subscription;
    private afAuthUser: firebase.User = null;
    private token: firebase.auth.IdTokenResult = null;
    /** invalidates an older afAuth.user callback that is still awaiting when a newer auth state arrives */
    private authSeq = 0;
    /** Date.now() of the last afAuth.user emission - for the header write diagnostics (msSinceAuth) */
    private lastAuthStateChangeAt: number | null = null;
    private lastForcedTokenRefreshAt: number | null = null;
    /**
     * the last `claimsUpdatedAt` stamp that triggered a forced token refresh - one refresh per stamp;
     * also persisted per uid (claims-refresh-storage.ts) so that an app restart does not repeat it
     */
    private lastClaimsUpdatedAt = 0;
    /** emits after a forced token refresh from a claims stamp, and when a new claims error arrives */
    private claimsChanged$ = new Subject<void>();
    /** read-only view of claimsChanged$ for consumers that cache a right derived from the token (CurrentPollService.isOwner) */
    public readonly claimsChanged: Observable<void> = this.claimsChanged$.asObservable();
    /** `at` of the last seen `claimsError`, so only a NEW failure wakes a waiter */
    private lastClaimsErrorAt = 0;
    /** code of the last seen `claimsError`, cleared as soon as the functions clear the record */
    private lastClaimsErrorCode: string | null = null;
    /**
     * Auth drop telemetry (workorder-poll-init-auth-drop-hotfix-2026-10-29, task 2): a user -> null emission
     * that was not our own signOut. Counts and times only - the uids stay in memory, never in a log line.
     */
    public authDropCount = 0;
    /** Date.now() of the last auth drop */
    public lastAuthDropAt: number | null = null;
    /** duration of the last finished drop: null -> user with the SAME uid; -1 = another uid came back */
    public lastAuthDropDurationMs: number | null = null;
    /** the first transitions of this app run were null -> user -> null, the drop within 5 s of the sign-in */
    public authDropOnStartup = false;
    /** set right before our own afAuth.signOut(), so that a wanted logout does not count as a drop */
    private ownSignOutPending = false;
    /** uid and Date.now() of the last non-null emission, null after a null emission */
    private signedInUid: string | null = null;
    private signedInAt: number | null = null;
    /** the open drop: uid that dropped and Date.now() of the drop, null when no drop is open */
    private droppedUid: string | null = null;
    private droppedAt: number | null = null;
    /** emissions since the app start and whether the first one was null (for authDropOnStartup) */
    private authEmissionCount = 0;
    private firstAuthEmissionWasNull = false;
    /** the warn `auth state dropped after sign-in` goes out once per session */
    private authDropReported = false;
    private authDropReportTimer: ReturnType<typeof setTimeout> | null = null;
    /** msSinceSignIn of the first drop, carried into the report */
    private firstDropMsSinceSignIn: number | null = null;
    /** cached persistence of the signed-in user (authPersistenceType), refreshed on every auth emission */
    private persistenceType: AuthPersistenceType = 'unknown';
    /**
     * Diagnostics of the first drop (workorder-auth-drop-ios-diagnostics-2026-11-03, task 2): the storage probe's summary and the
     * page context, captured at the moment of the user -> null emission - the SDK has removed the stored user by then, so
     * everything the probe saw BEFORE that is the only trace of the cause. Carried into the drop report.
     */
    private dropContext: (AuthDropContext & { tokenExpiresInMsAtDrop: number | null; clockSkewMs: number | null }) | null = null;
    /** `stsTokenManager.expirationTime` of the last non-null emission - tokenExpiresInMsAtDrop (the user is gone at the drop) */
    private lastTokenExpirationTime: number | null = null;
    /** the warn `auth storage anomaly` goes out once per session */
    private storageAnomalyReported = false;

    constructor(
        // TODO: Replace with tree shakable @angular/fire/auth
        private afAuth: AngularFireAuth,
        @Optional() private auth: Auth,
        private errorService: EssErrorService,
        private logger: LoggerService,
        private logUserContext: LogUserContextService,
        private databaseUserService: UserService,
        private router: Router,
        private trans: TranslationGeneralService,
        @Inject(LOCALE_ID) public locale: string,
        // no DI cycle: AuthErrorService only depends on logger, error, translation and ui-message services
        private authErrorService: AuthErrorService,
        // AccessGrantService depends on this service - resolved lazily per call (see accessGrantService)
        private injector: Injector,
        private uiMessage: UIMesageService,
    ) {
        // the UserService cannot inject the AuthService (circular DI), so it hands every dbUser emission over here
        this.databaseUserService.setClaimsListener(dbUser => { void this.onDbUserChanged(dbUser); });
        try {
            authStorageProbe.count('authServiceStarts');
            authStorageProbe.onAnomaly((summary, sinceStartMs) => this.reportStorageAnomaly(summary, sinceStartMs));
        } catch (error) {
            this.logger.warn('auth storage probe hook failed', error);
        }
        this.initService()
    }


    private async initService() {
        try {
            firebase.auth().languageCode = this.locale;
            this.subscribeAuthAndDbUser();
            await this.processRedirectResult();
        } catch (error) {
            this.logger.error('AuthService initService failed: ', error);
        }
    }
    /**
     * Subscribe to changes on afAuthUser and with every change we subscribe to databaseUserService.getDbUserObservable
     * so that we get the latest date in this.databaseUserService.currentDbUser
     */
    private subscribeAuthAndDbUser() {
        this.logger.log('subscribeAuthAndDbUser');
        //  this.afAuth.user.subscrib is called before this.afAuth.authState
        this.authUserSubscription = this.afAuth.user.subscribe(async (afAuthUser) => {
            const seq = ++this.authSeq;
            try {
                this.logger.log('subscribeAuthAndDbUser ', afAuthUser);
                this.lastAuthStateChangeAt = Date.now();
                this.updatePersistenceType(afAuthUser);
                this.trackAuthTransition(afAuthUser);
                // set before the first await so that this.uid is correct right away
                this.afAuthUser = afAuthUser;
                // server logs carry the uid from here on, so Error Reporting can count affected
                // users; the function hashes it, the raw uid never reaches Error Reporting
                this.logUserContext.setUserId(afAuthUser?.uid ?? null);
                // close the previous user's dbUser listen right away - the server would revoke it
                // (permission_denied) as soon as the new token is on the connection
                this.databaseUserService.unsubscribeDbUser();
                // Gemeinsamer Vertrag mit der Website (auth-state-storage.ts):
                // dieser Callback feuert bei Login, Logout, Anonym-Signup und
                // App-Start — der eine Hook deckt alle Zustandswechsel ab.
                // isAnonymous-Getter statt afAuthUser.isAnonymous roh, wegen der
                // E-Mail-Nuance (anonymer Account mit E-Mail zaehlt als 'user').
                if (afAuthUser == null) {
                    clearAuthState();
                } else {
                    writeAuthState(this.isAnonymous ? 'anon' : 'user');
                }
                await this.applyStashedGrantsFromSessionStorage(); // call here again in case the processRedirectResult failed
                if (seq !== this.authSeq) {
                    return; // a newer auth state arrived while awaiting - it owns the dbUser subscription
                }
                // cached token is enough: a fresh login delivers a fresh token anyway and claim changes
                // arrive via onDbUserChanged - must be loaded BEFORE the dbUser subscription so that
                // the stamp is compared against this user's token
                await this.loadIdTokenResult();
                if (seq !== this.authSeq) {
                    return;
                }
                this.databaseUserService.subscribeDbUser(afAuthUser?.uid);
            } catch (error) {
                this.logger.warn('subscribeAuthAndDbUser error: ', error);
            }
        });
    }

    /**
     * Counts auth drops (task 2 of workorder-poll-init-auth-drop-hotfix-2026-10-29): user -> null without our own
     * signOut, and how long it takes until a user is back. Prod finding (report of 2026-10-31): the drops are permanent
     * and almost all on WebKit - the stored user vanished from IndexedDB and the SDK signed the tab out; since
     * workorder-auth-persistence-local-first-2026-10-31 the user is kept in localStorage first (app.module.ts).
     * Synchronous, no round trip, never throws into the auth callback.
     */
    private trackAuthTransition(afAuthUser: firebase.User | null) {
        try {
            const now = Date.now();
            this.authEmissionCount++;
            if (this.authEmissionCount === 1) {
                this.firstAuthEmissionWasNull = afAuthUser == null;
            }
            if (afAuthUser) {
                this.ownSignOutPending = false;
                this.lastTokenExpirationTime = readTokenExpirationTime(afAuthUser);
                if (this.droppedAt !== null) {
                    const sameUid = afAuthUser.uid === this.droppedUid;
                    const durationMs = now - this.droppedAt;
                    this.lastAuthDropDurationMs = sameUid ? durationMs : -1;
                    this.droppedAt = null;
                    this.droppedUid = null;
                    this.reportAuthDrop({ returned: true, durationMs, sameUid });
                }
                if (afAuthUser.uid !== this.signedInUid) {
                    this.signedInAt = now;
                }
                this.signedInUid = afAuthUser.uid;
                return;
            }
            if (this.signedInUid === null) {
                return; // was signed out and stays signed out
            }
            if (this.ownSignOutPending) {
                this.ownSignOutPending = false;
            } else {
                this.authDropCount++;
                this.lastAuthDropAt = now;
                this.droppedAt = now;
                this.droppedUid = this.signedInUid;
                if (this.authDropCount === 1) {
                    this.dropContext = this.captureDropContext(now);
                }
                const msSinceSignIn = this.signedInAt === null ? null : now - this.signedInAt;
                if (this.authDropCount === 1 && this.authEmissionCount === 3 && this.firstAuthEmissionWasNull
                    && msSinceSignIn !== null && msSinceSignIn < AUTH_DROP_ON_STARTUP_MS) {
                    this.authDropOnStartup = true;
                }
                this.scheduleAuthDropReport(msSinceSignIn);
            }
            this.signedInUid = null;
            this.signedInAt = null;
        } catch (error) {
            this.logger.warn('trackAuthTransition failed', error);
        }
    }

    /**
     * The first drop of the session is reported once: when the user is back (durationMs, sameUid), or after
     * AUTH_DROP_REPORT_WAIT_MS without a user (returned: false) - the SDK removes the stored user on a synced
     * logout, so a drop can also be permanent, and only this case says whether the poll-init retry can help.
     */
    private scheduleAuthDropReport(msSinceSignIn: number | null) {
        if (this.authDropReported || this.authDropReportTimer !== null) return;
        this.firstDropMsSinceSignIn = msSinceSignIn;
        this.authDropReportTimer = setTimeout(() => {
            this.authDropReportTimer = null;
            if (this.droppedAt !== null) {
                this.reportAuthDrop({ returned: false, durationMs: null, sameUid: null });
            }
        }, AUTH_DROP_REPORT_WAIT_MS);
    }

    private reportAuthDrop(outcome: { returned: boolean; durationMs: number | null; sameUid: boolean | null }) {
        if (this.authDropReported) return;
        this.authDropReported = true;
        if (this.authDropReportTimer !== null) {
            clearTimeout(this.authDropReportTimer);
            this.authDropReportTimer = null;
        }
        try {
            warnToServer(this.logger, AUTH_DROP_MESSAGE, {
                ...outcome,
                msSinceSignIn: this.firstDropMsSinceSignIn,
                onStartup: this.authDropOnStartup,
                persistence: this.authPersistenceType,
                // the diagnostics of the drop (task 2 of workorder-auth-drop-ios-diagnostics-2026-11-03), captured at the drop
                ...(this.dropContext ?? {}),
            });
        } catch (error) {
            this.logger.warn(AUTH_DROP_MESSAGE, outcome, error);
        }
    }

    /**
     * The probe's view and the page context at the moment of the drop (workorder-auth-drop-ios-diagnostics-2026-11-03, task 2):
     * `probe` (nullReads / valueChanges / removedBy / storageEvents / last events - K1 vs. K2), `keyBeforeDrop` (K1),
     * `lastAuthHttpError` (K3), `tokenExpiresInMsAtDrop` and `clockSkewMs` (K3 / device clock), `navType`, `standalone`,
     * `inApp`, `visibility` + `hiddenSinceStart`, `tabs` (K2), `storageQuotaErr`. Never throws into the auth callback.
     */
    private captureDropContext(now: number): AuthService['dropContext'] {
        try {
            return {
                ...authStorageProbe.dropContext(),
                tokenExpiresInMsAtDrop: this.lastTokenExpirationTime === null ? null : this.lastTokenExpirationTime - now,
                clockSkewMs: this.readClockSkew(),
            };
        } catch (error) {
            this.logger.warn('captureDropContext failed', error);
            return null;
        }
    }

    /** server clock minus client clock as the database SDK reports it, null before the first connection */
    private readClockSkew(): number | null {
        try {
            const skew = this.injector.get(ServerTimeService)?.clockSkewMs;
            return typeof skew === 'number' ? skew : null;
        } catch {
            return null;
        }
    }

    /**
     * The first storage anomaly of the session (task 2): the probe read `null` for the stored user although it had been there,
     * or another context of the origin removed it - reported once, ALSO when no drop follows, so that the share of sessions
     * with such reads but without a drop (the base rate on WebKit) becomes visible. A following drop carries the same
     * summary in `probe` of the drop report.
     */
    private reportStorageAnomaly(summary: ProbeSummary, sinceStartMs: number): void {
        if (this.storageAnomalyReported) return;
        this.storageAnomalyReported = true;
        try {
            warnToServer(this.logger, AUTH_STORAGE_ANOMALY_MESSAGE, { probe: summary, sinceStartMs, dropFollowed: 'pending' });
        } catch (error) {
            this.logger.warn(AUTH_STORAGE_ANOMALY_MESSAGE, error);
        }
    }

    /**
     * Where the Auth SDK keeps the signed-in user. app.module.ts (provideAuth / initializeAuth) sets the hierarchy
     * localStorage -> IndexedDB -> sessionStorage (workorder-auth-persistence-local-first-2026-10-31); the compat
     * instance (AngularFireAuth, firebase.auth()) reuses that one instance. Read without SDK internals: the stored
     * user sits under a `firebase:authUser:` key in localStorage (`local`) or sessionStorage (`session`), a user without
     * such a key is in IndexedDB (`idb`); `none` before any sign-in, `unknown` when the storage throws or nothing
     * emitted yet. Cached per emission (updatePersistenceType) - a drop (user -> null) keeps the value of the dropped
     * user, so the drop report says where that user had been stored.
     */
    public get authPersistenceType(): AuthPersistenceType {
        return this.persistenceType;
    }

    /**
     * Refreshes the cached persistence type on every auth emission: a user -> where the stored user sits, no user
     * before any sign-in -> `none`, no user after a sign-in -> the last value stays. Reads in try/catch (a blocked
     * storage throws on access), never throws into the auth callback.
     */
    private updatePersistenceType(afAuthUser: firebase.User | null) {
        if (afAuthUser == null) {
            if (this.persistenceType === 'unknown') {
                this.persistenceType = 'none';
            }
            return;
        }
        this.persistenceType = detectAuthPersistence();
    }

    /**
     * The drop part of the poll-init diagnostics, no ids: `authDrops: n; lastDropMs: <ms>|-1|open|none; persistence: …`
     * (-1 = another uid came back, open = the last drop has not ended yet, none = no drop in this app run).
     */
    public get authDropDiagnostics(): string {
        const lastDropMs = this.droppedAt !== null ? 'open' : (this.lastAuthDropDurationMs ?? 'none');
        return 'authDrops: ' + this.authDropCount + '; lastDropMs: ' + lastDropMs + '; persistence: ' + this.authPersistenceType;
    }

    /**
     * Resolves with the uid as soon as the Auth SDK holds a signed-in user again, null after
     * timeoutMs. The auth state can drop to null for a moment after a successful sign-in
     * (persistence sync, e.g. IndexedDB on iOS/Safari) and come back with the same uid -
     * see workorder-poll-init-auth-drop-hotfix-2026-10-29.
     * No sign-in of its own: a second anonymous sign-in would create a NEW uid and cut the user off from
     * the previous one. Never throws.
     */
    public async waitForSignedInUser(timeoutMs: number): Promise<string | null> {
        let uid: string | null;
        try {
            uid = await firstValueFrom(this.afAuthUser$.pipe(
                filter(user => user != null),
                map(user => user.uid),
                timeout({ first: timeoutMs, with: () => of(null) })
            ));
        } catch (error) {
            this.logger.warn('waitForSignedInUser failed', error);
            return null;
        }
        if (uid === null) return null;
        // authState can emit before the SDK-internal currentUser is set, and the database connection only
        // gets the token with the SDK state - poll briefly until it is there
        for (let attempt = 0; attempt < SDK_USER_POLL_ATTEMPTS && this.sdkCurrentUserUid === null; attempt++) {
            await new Promise(resolve => setTimeout(resolve, SDK_USER_POLL_MS));
        }
        return this.sdkCurrentUserUid;
    }

    private async loadUser(uid: string) {
        this.isLoading = true;
        try {
            if (!this.authUserSubscription) {
                this.subscribeAuthAndDbUser();
            }
            this.afAuthUser = await firstValueFrom(this.afAuthUser$);
            // shares the create with the dbUser subscription if that one is already running for this uid
            await this.databaseUserService.ensureDbUser(uid);
        } catch (error) {
            throw (this.errorService.newError('Error loading user' + error))
        } finally {
            this.isLoading = false;
        }
    }

    /**
     *
     * SignIn &  Login
     *
     */

    /**
     * @param navigate false keeps a stored navigation target for later (the legacy gate: the password login of an
     * unconfirmed account must not leave the login form before the code - the gate's confirm navigates)
     */
    private async handleAfterSignIn(user: firebase.User, navigate = true) {
        this.logger.log('handleAfterSignIn', navigate);
        this.afAuthUser = user;
        await this.loadUser(this.afAuthUser.uid);
        if (navigate) {
            this.handleNavigation();
        }
    }

    public async signUpAnonymous(displayName: string) {
        this.logger.log('signUpAnonymous, displaynname: ' + displayName);
        const signInResult = await this.afAuth.signInAnonymously();
        if (!signInResult || !signInResult.user) {
            throw this.errorService.newError(this.trans.translate('internalError'));
        }
        this.afAuthUser = signInResult.user;
        this.logger.log('signUpAnonymous: signInResult: ', signInResult, this.afAuthUser.uid);
        await this.loadUser(this.afAuthUser.uid);
        this.logger.log('signUpAnonymous: updateUserDoc done ');
    }

    /**
     * The password login - the collapsed second way behind `passwordLoginEnabled` since code auth P3 (removed in P4).
     * Returns `emailVerified` of the fresh sign-in. A verified account navigates as before: to a target stored by the
     * AuthGuard, else to `defaultTarget` (the login form passes My Events). An unconfirmed account is signed in but
     * NOT navigated and the default is not stored either (a stored AuthGuard target stays for the gate's confirm,
     * which runs per code and verifies the address server-side, E7) - the login form shows the gate then; a default
     * stored here would otherwise linger in sessionStorage when the gate is abandoned and redirect the next app
     * start. GA4 `login`/`password` counts every password login (the P4 measurement).
     */
    public async signInWithEmail(email: string, password: string, defaultTarget?: string): Promise<boolean> {
        this.logger.log('signInWithEmail')
        const signInResult = await this.afAuth.signInWithEmailAndPassword(email, password);
        const emailVerified = signInResult.user?.emailVerified === true;
        if (emailVerified && defaultTarget) {
            this.setNavigateToUrlIfNotSet(defaultTarget);
        }
        await this.handleAfterSignIn(signInResult.user, emailVerified)
        this.trackAuthEvent('login', 'password');
        return emailVerified;
    }

    /**
     * Step 2 of the code sign-in (code auth P2, task 2; concept §4.2): trades address + code for a custom token and
     * signs in with it. The server decides what the code does from the intent of the `request` and the session
     * (P1 report §3); the client only has to be ready for all outcomes:
     *  - the session is anonymous -> its polls, answers and held grants are collected and stashed BEFORE the
     *    confirm (the custom-token sign-in ends the anonymous session; a failing collection does not stop the
     *    sign-in, D2), so that the server can `upgrade` or `merge`;
     *  - the bearer goes along whenever there IS a session (P3: anonymous or signed in) - it must be the same
     *    session as at `request` (P1 report §3): `upgrade` needs the anonymous one, the legacy gate (`login` out of
     *    a signed-in password session, P1a #8) and `changeEmail` the signed-in one. The stash stays anonymous-only;
     *  - `signInWithCustomToken` always - also for `upgraded`, where the uid stays: afterwards `isAnonymous` is
     *    false and the token carries `email` + `email_verified`; one forced token refresh makes sure of it;
     *  - `merge`: the token belongs to the existing account of the address - the stash is applied under that uid,
     *    1:1 like the provider merge (dbUser re-subscribed, BigQuery sync, snackbar only on failure);
     *  - `upgraded`: same uid, nothing to adopt - the stash is dropped;
     *  - `emailChanged` (P3): same uid, the address of the user record changed - only the user record trigger
     *    (updateUserToTriggerSyncWithBigQuery, no export change); nothing was stashed (never anonymous here);
     *  - `created` / `signedIn`: a fresh session, the stash of an anonymous session (if any) lapses like today's
     *    abort. `signedIn` out of a signed-in session is the gate (same uid, now verified) or an account switch.
     * A failing confirm leaves the session untouched (nothing signed in, the stash stays for a retry).
     * GA4: `sign_up`/`code` for created + upgraded, `login`/`code` for signedIn + merge + emailChanged. handleAfterSignIn
     * navigates to a target stored before (the AuthGuard's, or `myPolls` set by the login form right before the
     * confirm; none for the plain sign-up: the page stays and shows the welcome page).
     */
    public async signInWithAuthCode(email: string, code: string): Promise<AuthCodeOutcome> {
        const hadSession = this.isLoggedIn;
        const wasAnonymous = this.isAnonymous;
        const fromUid = this.uid;
        this.logger.log('signInWithAuthCode', hadSession, wasAnonymous, fromUid);
        const collected = wasAnonymous ? await this.collectAndStashSessionItems() : true;
        const { outcome, token } = await this.authCodeApiService.confirm({ email, code }, hadSession);
        const credential = await this.afAuth.signInWithCustomToken(token);
        if (!credential?.user) {
            throw this.errorService.newError('signInWithAuthCode: no user after the custom token sign-in');
        }
        await this.handleAfterSignIn(credential.user);
        // the token of the custom sign-in may predate the account update (email, email_verified) - one refresh
        await this.loadIdTokenResult(true);
        if (outcome === 'merge') {
            this.databaseUserService.unsubscribeDbUser();
            this.databaseUserService.subscribeDbUser(this.uid);
            const result = await this.accessGrantService.applyStashedGrants(this.uid);
            await this.databaseUserService.updateUserToTriggerSyncWithBigQuery(this.uid);
            this.notifyAdoptResult(collected ? result : { accepted: 0, noop: 0, failed: -1 });
        } else if (outcome === 'upgraded') {
            if (fromUid !== this.uid) {
                this.logger.warn('signInWithAuthCode: upgraded, but the uid changed', fromUid, this.uid);
            }
            // the same uid holds everything already - the stash of step 1 has nothing to adopt
            this.accessGrantService.clearStash();
            await this.databaseUserService.updateUserToTriggerSyncWithBigQuery(this.uid);
        } else if (outcome === 'emailChanged') {
            if (fromUid !== this.uid) {
                this.logger.warn('signInWithAuthCode: emailChanged, but the uid changed', fromUid, this.uid);
            }
            // the address on the user record changed - the existing user record trigger only (no export change)
            await this.databaseUserService.updateUserToTriggerSyncWithBigQuery(this.uid);
        }
        if (outcome === 'created' || outcome === 'upgraded') {
            this.trackAuthEvent('sign_up', 'code');
        } else if (outcome === 'signedIn' || outcome === 'merge' || outcome === 'emailChanged') {
            this.trackAuthEvent('login', 'code');
        }
        return outcome;
    }

    public async signInWithProvider(provider: AuthProvider) {
        const firebaseProvider = this.convertProvider(provider);
        const emulateNoPopUp = false;  // for testing purpose only
        try { // try always first popup
            if (emulateNoPopUp) {
                const error = { message: 'emulateNoPopUp', code: "auth/popup-blocked" }
                throw (error)
            }
            let result = await signInWithPopup(this.auth, firebaseProvider, browserPopupRedirectResolver);
            await this.handleAfterSignIn(<firebase.User>result.user)
            this.trackAuthEvent('login', firebaseProvider.providerId);
        } catch (popUpError) {
            if (popUpError.code === 'auth/popup-blocked') {
                this.logger.log('signInWithProvider: Popup Blocked');
                try {
                    await this.afAuth.signInWithRedirect(firebaseProvider);
                    // redirect triggers a reload so the program flow stops here
                    //  so we cant call handleAfterSignIn , instead this is called via processRedirectResult and getRedirectResult
                } catch (redirectError) {
                    if (isNetworkRequestFailed(redirectError)) {
                        // keeps its code, so that AuthErrorService shows "no connection" instead of the generic text
                        throw redirectError;
                    }
                    let message = 'Pop-up window has been blogged. SignInWithRedirect failed. Please try again or enable popup windows in your browser. (Error-code:' + JSON.stringify(redirectError) + ')';
                    throw this.errorService.newError(message)
                }
            }
            else {// popup failed but not because of popup-blocked
                throw popUpError
            }
        }
    }

    /**
     * Anonymous session signs in with a provider (workorder 1.18 §B.2): same pattern as the password
     * merge. The stash is written BEFORE the popup / redirect - it has to survive the redirect reload,
     * where `processRedirectResult` / the auth emission apply it (applyStashedGrantsFromSessionStorage).
     */
    public async mergeWithProviderAccount(provider: AuthProvider) {
        this.logger.log('mergeWithProviderAccount', provider);
        const collected = await this.collectAndStashSessionItems();
        await this.signInWithProvider(provider);
        this.databaseUserService.unsubscribeDbUser();
        this.databaseUserService.subscribeDbUser(this.uid);
        const result = await this.accessGrantService.applyStashedGrants(this.uid);
        await this.databaseUserService.updateUserToTriggerSyncWithBigQuery(this.uid);
        this.notifyAdoptResult(collected ? result : { accepted: 0, noop: 0, failed: -1 });
    }

    /** AccessGrantService depends on this service: resolved lazily, so that the DI graph stays free of cycles */
    private get accessGrantService(): AccessGrantService {
        return this.injector.get(AccessGrantService);
    }

    /** AuthCodeApiService depends on this service (the bearer): resolved lazily like the AccessGrantService */
    private get authCodeApiService(): AuthCodeApiService {
        return this.injector.get(AuthCodeApiService);
    }

    /**
     * The GA4 login / sign-up counter (code auth P1, task 7): `login` resp. `sign_up` with `method` ("password",
     * a provider id, "code" from P2 on). GoogleAnalyticsService depends on this service, so it is resolved lazily
     * like the AccessGrantService; a failing emit never fails a sign-in.
     */
    private trackAuthEvent(event: 'login' | 'sign_up', method: string): void {
        try {
            const analytics = this.injector.get(GoogleAnalyticsService);
            if (event === 'login') {
                analytics.emitLogin(method);
            } else {
                analytics.emitSignUp(method);
            }
        } catch (error) {
            this.logger.warn('trackAuthEvent: analytics not available', event, method, error);
        }
    }

    /**
     * Step 1 of both merge paths: everything the anonymous session holds, as the anonymous user, parked
     * for the sign-in. False when the collection itself failed (list read) - the sign-in goes on, the
     * caller tells the user afterwards (D2: no fallback to a delegation).
     */
    private async collectAndStashSessionItems(): Promise<boolean> {
        const fromUid = this.uid;
        if (!fromUid) {
            this.logger.warn('mergeAccount: nobody signed in, nothing to adopt');
            return true;
        }
        try {
            const items = await this.accessGrantService.collectSessionItems(fromUid);
            this.accessGrantService.stashSessionItems(fromUid, items);
            return true;
        } catch (error) {
            this.logger.warn('mergeAccount: session items not collected, signing in without them', fromUid, error);
            return false;
        }
    }

    /**
     * Snackbar only when the adopt could not take everything over (workorder 1.18 D2 / D6): `failed > 0`
     * or `-1` (nothing collected). A clean run stays silent - My Events shows the result.
     */
    private notifyAdoptResult(result: AdoptResult): void {
        if (!result || result.failed === 0) return;
        this.uiMessage.warning(this.trans.translate('auth.adoptGrantsFailed'));
    }

    /**
     * Because redirect triggers a reload so the program flow stops
     * is called in the constructor to process the redirect result
     */
    private async processRedirectResult(): Promise<void> {
        this.logger.log('processRedirectResult')
        this.isLoading = true;
        try {
            if (this.isLoggedIn) {
                await this.applyStashedGrantsFromSessionStorage();
            }
            await this.checkRedirectResultForErrors()
            this.handleNavigation();
        } catch (error) {
            if (UNSUPPORTED_ENVIRONMENT_CODES.includes(error?.code)) {
                // the compat SDK checks web storage before it even looks for a redirect result, so on
                // EVERY app start in a browser with blocked storage; nobody clicked a sign-in button
                // here (and the translations are not loaded yet) - the click path shows the message
                this.logger.warn('processRedirectResult: sign-in not supported in this environment', error.code);
                return;
            }
            // after the redirect reload nobody else is listening - without a message the visitor
            // lands back on the page signed out and silent; handleError logs a warning itself
            this.authErrorService.handleError(error);
        } finally {
            this.isLoading = false;
        }
    }

    private async checkRedirectResultForErrors() {
        let userCredential
        try {
            userCredential = await this.afAuth.getRedirectResult()
            this.logger.log('checkRedirectResultForErrors', userCredential)
            if (userCredential?.user) {
                // the redirect form of the provider sign-in lands here after the reload - the popup form counts in signInWithProvider
                this.trackAuthEvent('login', userCredential.additionalUserInfo?.providerId ?? userCredential.user.providerData?.[0]?.providerId ?? 'provider');
            }
        } catch (error) {
            let errorMessage = ""
            if (error.code === 'auth/account-exists-with-different-credential') {
                // AuthErrorService has a translated text for this code
                throw error;
            } else if (error.code === "auth/missing-or-invalid-nonce") { // this is a conflict with loading the user  - it soes not  cause an issue so we ignore
                errorMessage = "Error can be ignored"
                this.logger.warn(errorMessage, error, userCredential);
            }
            else {
                // keep the original FirebaseError - AuthErrorService switches on `code`, and a fresh
                // Error would lose it (the code only survived inside the message before)
                throw error;
            }
        }
    }

    /**
     * The redirect path of the provider merge (workorder 1.18 §B.3): after the redirect reload the stash
     * from mergeWithProviderAccount is still in sessionStorage - applied to the signed-in user here, from
     * processRedirectResult and from every auth emission. A logout emission keeps the stash for the next
     * signed-in one; the "still the anonymous source user" guard sits in applyStashedGrants.
     */
    private async applyStashedGrantsFromSessionStorage(): Promise<void> {
        if (!this.uid) {
            return;
        }
        const result = await this.accessGrantService.applyStashedGrants(this.uid);
        this.notifyAdoptResult(result);
    }

    /**
     * Helper functions
     */

    /**
     * @param forceRefresh false (default) = cached token unless expired; true = one round trip to
     * securetoken.googleapis.com, which is rate limited (auth/quota-exceeded) - only onDbUserChanged
     * and the waitForClaims fallback force
     */
    public async loadIdTokenResult(forceRefresh = false) {
        this.token = await this.afAuthUser?.getIdTokenResult(forceRefresh);
        this.logger.log('loadIdTokenResult', forceRefresh, this.token?.claims);
    }

    /**
     * The ID token of the signed-in user for an `Authorization: Bearer` header (email settings auth mode,
     * `EmailSettingsApiService`); cached unless expired, null without a user. `forceRefresh` is one round
     * trip to securetoken.googleapis.com (rate limited) - only ensureVerifiedToken forces. Never log the token.
     */
    public async getIdToken(forceRefresh = false): Promise<string | null> {
        return (await this.afAuthUser?.getIdToken(forceRefresh)) ?? null;
    }

    /**
     * Makes sure the session reflects an e-mail verification that happened elsewhere (email settings P3
     * follow-up, `workorder-email-settings-p3-nachtrag-verified-token-2026-10-23.md`). Two layers go stale
     * after the user clicks the verification link, usually in another tab:
     *  1. the `firebase.User` of this tab keeps `emailVerified = false` until `reload()` - the profile would
     *     pass it to the card and show the "verify first" hint without asking the endpoint;
     *  2. after a page reload the user is fresh, but the cached ID token (valid up to an hour) still carries
     *     `email_verified: false` - the endpoint answers 403 `email-not-verified` and the card shows the
     *     same hint. Only a logout/login or the token's expiry would end it.
     * So: reload the user; when the address is verified, read the cached token's claims and force exactly
     * one refresh if `email_verified` is not true yet. Provider accounts (Google etc.) come verified in both
     * layers, so they pay only the reload. Returns `user.emailVerified` (false without a user). Failures
     * (offline, ...) are a warning and return the current `emailVerified` - never a throw; the card's own
     * states handle the rest. Neither the token nor its claims are logged.
     */
    public async ensureVerifiedToken(): Promise<boolean> {
        try {
            const user = await this.reloadUser();
            if (!user) {
                return false;
            }
            if (user.emailVerified !== true) {
                return false;
            }
            const tokenResult = await user.getIdTokenResult();
            if (tokenResult?.claims?.['email_verified'] !== true) {
                this.logger.log('ensureVerifiedToken: the cached token predates the verification, refreshing once');
                await user.getIdToken(true);
            }
            return user.emailVerified === true;
        } catch (error) {
            this.logger.warn('ensureVerifiedToken failed', String((error as Error)?.message ?? error).slice(0, 120));
            return this.emailVerified;
        }
    }

    /**
     * Called by the UserService with every emission of the dbUser subscription. The functions stamp
     * `claimsUpdatedAt` after setCustomUserClaims; a stamp younger than the token means the token
     * lacks a claim -> exactly one forced refresh (also covers a claim set while the app was closed).
     * https://firebase.google.com/docs/auth/admin/custom-claims#propagate_custom_claims_to_the_client
     */
    public async onDbUserChanged(dbUser: DbUser | null): Promise<void> {
        this.readClaimsError(dbUser);
        const stamp = dbUser?.claimsUpdatedAt ?? 0;
        const uid = this.uid ?? dbUser?.uid;
        const issuedAt = this.token?.issuedAtTime ? Date.parse(this.token.issuedAtTime) : 0;
        // issuedAtTime has second granularity, the stamp milliseconds: a token refreshed in the same
        // second as the stamp looks older than the stamp - the persisted "already handled" stamp
        // keeps the next app start from refreshing a second time
        const handled = Math.max(this.lastClaimsUpdatedAt, uid ? readClaimsRefreshedAt(uid) : 0);
        if (!stamp || stamp <= issuedAt || stamp <= handled) {
            return;
        }
        this.lastClaimsUpdatedAt = stamp;
        if (uid) {
            writeClaimsRefreshedAt(uid, stamp);
        }
        try {
            await this.loadIdTokenResult(true);
            this.claimsChanged$.next();
        } catch (error) {
            this.logger.warn('onDbUserChanged: token refresh failed', error);
        }
    }

    /**
     * The functions report a failed `setCustomUserClaims` on the user record. Without this the
     * delegation is written but never becomes a claim, and the user only sees the "no access" page.
     * Only a NEW failure (fresh `at`) counts - the stored one was already seen on an earlier emission.
     */
    private readClaimsError(dbUser: DbUser | null): void {
        const at = dbUser?.claimsError?.at ?? 0;
        if (!at) {
            // cleared by a later successful claims write
            this.lastClaimsErrorAt = 0;
            this.lastClaimsErrorCode = null;
            return;
        }
        if (at === this.lastClaimsErrorAt) {
            return;
        }
        this.lastClaimsErrorAt = at;
        this.lastClaimsErrorCode = dbUser.claimsError.code ?? 'unknown';
        this.logger.warn('onDbUserChanged: the functions could not write the custom claims', this.lastClaimsErrorCode);
        this.claimsChanged$.next(); // wake a waiter: waiting longer would not help
    }

    /**
     * Resolves as soon as the token contains the delegation of fromUid (arrives via onDbUserChanged),
     * or as soon as the functions report that the claims write failed. Only a plain timeout still
     * costs one fallback forced refresh, and then reports a failure already stored on the record.
     * @param timeoutMs 0 when no backend run is on its way (the delegation was already stored) - the
     * fallback refresh then decides right away instead of burning the full wait.
     */
    public async waitForClaims(fromUid: string, timeoutMs = 15000): Promise<ClaimsWaitResult> {
        if (this.getDelegatedUserIds().includes(fromUid)) {
            return { granted: true };
        }
        // the stored failure (if any) belongs to an earlier attempt - only a newer one is ours
        const errorAtBefore = this.lastClaimsErrorAt;
        const hasNewError = () => this.lastClaimsErrorAt !== errorAtBefore;
        await firstValueFrom(this.claimsChanged$.pipe(
            filter(() => this.getDelegatedUserIds().includes(fromUid) || hasNewError()),
            map(() => true),
            timeout({ first: timeoutMs, with: () => of(false) })
        ));
        if (this.getDelegatedUserIds().includes(fromUid)) {
            return { granted: true };
        }
        if (hasNewError()) {
            return { granted: false, errorCode: this.lastClaimsErrorCode };
        }
        try {
            await this.loadIdTokenResult(true); // the only fallback
        } catch (error) {
            this.logger.warn('waitForClaims: fallback token refresh failed', error);
        }
        if (this.getDelegatedUserIds().includes(fromUid)) {
            return { granted: true };
        }
        this.logger.warn('waitForClaims: claim not visible after timeout', fromUid);
        if (this.lastClaimsErrorCode) {
            // nothing arrived and the record still carries a failure for this account - reopening
            // the same link never triggers the backend again, so this is the verdict, not a stale value
            return { granted: false, errorCode: this.lastClaimsErrorCode };
        }
        return { granted: false };
    }

    private handleNavigation() {
        this.logger.log('handleNavigation: ', this.navigateToUrl);
        if (this.navigateToUrl) {
            this.router.navigate([this.navigateToUrl]);
        }
        this.navigateToUrl = null;
        this.logger.log('redirectUrl reset');
    }


    private convertProvider(
        provider: AuthProvider
    ): firebase.auth.AuthProvider {
        switch (provider) {
            case AuthProvider.Google:
                return new firebase.auth.GoogleAuthProvider();
            case AuthProvider.Facebook:
                return new firebase.auth.FacebookAuthProvider();
            case AuthProvider.Twitter:
                return new firebase.auth.TwitterAuthProvider();
            case AuthProvider.Github:
                return new firebase.auth.GithubAuthProvider();
            default:
                throw this.errorService.newError(provider + ' is not avaible as authentication provider')
        }
    }


    /**
     * General functions
     *
     * The action-code methods below serve `user/management` for the in-flight links of mails sent before the
     * code-auth cutover (`mode=verifyEmail`, `mode=resetPassword`) - they fall with P4. New verification or
     * reset mails are not sent any more (code auth P3: sendNewVerificationEmail / requestPasswordResetEmail gone).
     */

    public async getEmailFromActionCode(code: string): Promise<string> {
        return (await this.afAuth.checkActionCode(code)).data.email

    }

    public async verifyEmail(code: string) {
        await this.afAuth.applyActionCode(code)
    }

    public async submitNewPassword(code: string, newPassword: string) {
        await this.afAuth.confirmPasswordReset(code, newPassword)
    }

    async logOut(): Promise<void> {
        try {
            this.isLoading = true
            this.afAuthUser = null;
            this.token = null;
            await this.databaseUserService.logout();
            await this.authUserSubscription.unsubscribe();
            this.authUserSubscription = null;
            this.ownSignOutPending = true; // a wanted logout is no auth drop
            authStorageProbe.markAppSignOut(); // the storage probe classifies the following removeItem as sdkSignOut
            await this.afAuth.signOut();
        } catch (error) {
            throw this.errorService.newError('Logout failed. ' + error)
        } finally {
            this.isLoading = false;
        }
    }

    /**
     *
     * getter and setters
     *
     */

    get navigateToUrl(): string {
        const returnValue = window.sessionStorage.getItem('redirectUrl')
        this.logger.log('get redirectUrl', returnValue)
        return window.sessionStorage.getItem('redirectUrl') // needs to perist for the loginWithRedirect
    }

    set navigateToUrl(url: string) {
        this.logger.log('set redirectUrl', url)
        if (url) {
            window.sessionStorage.setItem('redirectUrl', url) // needs to perist for the loginWithRedirect
        } else {
            sessionStorage.removeItem("redirectUrl");
        }
    }

    public setNavigateToUrlIfNotSet(url: string) { // we don't want to overwright what was set in authguard when coming from an external link
        if (!this.navigateToUrl) {
            this.navigateToUrl = url;
        }
    }

    /**
     * get driecty the observable to the auth user
     * this is need for reactive forms to updated values after ngInit
     */
    public get afAuthUser$(): Observable<firebase.User | null> {
        // return this.afAuth.user;
        return this.afAuth.authState;
    }

    /**
     * Can be userd in template *ngIf ; the result we change after the user is loaded
     */
    public get isLoggedIn(): boolean {
        if (this.afAuthUser) {
            return true;
        }
        return false;
    }

    /**
     * before creating an anonymous user when we enter a page we want to wait for the promise to make sure if the user is logged in
     * @returns
     */
    public async getIsLoggedIn(): Promise<boolean> {
        const afAuthUser = await firstValueFrom(this.afAuthUser$);
        if (afAuthUser) {
            return true;
        }
        return false;
    }

    public get isAnonymous(): boolean {
        if (this.afAuthUser?.isAnonymous) {
            // if an email upgrades the anonymous account but did no set the password the currentUser.isAnonymous
            // remains true but we want to treat him already like a proper user
            if (this.email == null || this.email === '') {
                return true;
            }
        }
        return false;
    }

    public get email(): string {
        return this.afAuthUser?.email;
    }

    /**
     * Re-reads the signed-in user from the backend, e.g. to pick up an e-mail verification that
     * happened in another tab or via an already consumed action link. Waits for the auth state to be
     * restored first, because a page may ask right after its load. Null when nobody is signed in.
     */
    public async reloadUser(): Promise<firebase.User | null> {
        const user = this.afAuthUser ?? await firstValueFrom(this.afAuthUser$);
        if (!user) {
            return null;
        }
        await user.reload();
        return user;
    }

    public get hasEmail(): boolean {
        if (this.afAuthUser?.email) return true
        return false
    }

    public get providerIds(): string[] {
        if (this.afAuthUser?.providerData) {
            return this.afAuthUser?.providerData.map(
                (element) => element.providerId
            );
        }
    }

    public get uid(): string {
        return this.afAuthUser?.uid;
    }

    /**
     * Diagnostics for a denied header write (workorder-persist-new-poll-single-flight-2026-09-22,
     * task 5) - all synchronous, no round trip:
     * the uid the Auth SDK holds right now (afAuthUser is our copy from the user subscription and can
     * lag behind or run ahead of it), the issue time of the cached id token and the time since the
     * last auth state emission.
     */
    public get sdkCurrentUserUid(): string | null {
        try {
            return firebase.auth().currentUser?.uid ?? null;
        } catch {
            return null;
        }
    }

    public get tokenIssuedAtTime(): string | undefined {
        return this.token?.issuedAtTime;
    }

    /**
     * ms until the id token the Auth SDK holds right now expires (negative = expired), null without a user. Read from
     * the user's serialized token manager, synchronous and without a refresh: `getIdTokenResult()` would refresh an
     * expired token and hide exactly what the diagnostics look for (a skewed clock, a stale connection token).
     */
    public get tokenExpiresInMs(): number | null {
        try {
            const user = firebase.auth().currentUser as unknown as { toJSON?: () => { stsTokenManager?: { expirationTime?: number } } } | null;
            const expirationTime = user?.toJSON?.()?.stsTokenManager?.expirationTime;
            return typeof expirationTime === 'number' ? expirationTime - Date.now() : null;
        } catch {
            return null;
        }
    }

    /**
     * One forced id token refresh after a rule rejection with a signed-in user (header persist, poll init): the SDK
     * hands the new token to the database connection, which re-authenticates with it. Capped at once per
     * TOKEN_REFRESH_MIN_INTERVAL_MS - a rejection loop must not hammer securetoken (auth/quota-exceeded). Resolves to
     * true when a refresh went through, false when skipped (no user, cap) or failed (warn, never a throw).
     */
    public async forceTokenRefreshOnce(): Promise<boolean> {
        const user = this.afAuthUser;
        if (!user) return false;
        const now = Date.now();
        if (this.lastForcedTokenRefreshAt !== null && now - this.lastForcedTokenRefreshAt < TOKEN_REFRESH_MIN_INTERVAL_MS) {
            return false;
        }
        this.lastForcedTokenRefreshAt = now;
        try {
            await user.getIdToken(true);
            return true;
        } catch (error) {
            this.logger.warn('forceTokenRefreshOnce failed', String((error as Error)?.message ?? error).slice(0, 120));
            return false;
        }
    }

    public get msSinceAuthStateChange(): number | null {
        return this.lastAuthStateChangeAt === null ? null : Date.now() - this.lastAuthStateChangeAt;
    }

    public async getUid(): Promise<string> {
        const afAuthUser = await firstValueFrom(this.afAuthUser$);
        if (afAuthUser) {
            return afAuthUser.uid;
        }
        return null;
    }

    public get displayName(): string {
        return this.afAuthUser?.displayName;
    }

    public getDelegatedUserIds(): string[] {
        const delegationsString = this.token?.claims?.delegations;
        if (delegationsString === null || delegationsString === undefined) {
            return [];
        }
        const delegations = delegationsString.split(',');
        return delegations;
    }

    public async getAllUserIds(): Promise<string[]> {
        await this.loadIdTokenResult(); // cached - claim changes arrive via onDbUserChanged
        let pollOwnerIds: string[] = [];
        const delegatedUserIds = this.getDelegatedUserIds();
        pollOwnerIds = [...delegatedUserIds];
        pollOwnerIds.push(this.uid);
        return pollOwnerIds

    }

    public getProducts(): string[] | null {
        return this.token?.claims?.products;
    }

    public hasAProduct(): boolean {
        if (
            !this.token?.claims?.products ||
            this.token?.claims?.products.length === 0
        ) {
            return false;
        }
        return true;
    }

    public hasEditRights(uid: string, ownerId: string): boolean {
        const delegatedUids = this.getDelegatedUserIds();
        if (uid === ownerId || delegatedUids.includes(ownerId)) {
            this.logger.log('hasEditRights true')
            return true;
        }
        this.logger.log('hasEditRights false')
        return false;
    }

    public async setDisplayName(name: string): Promise<void> {
        await this.afAuthUser?.updateProfile({ displayName: name });
        await this.databaseUserService.updateUserToTriggerSyncWithBigQuery(this.uid)
        return
    }

    public get hasPasswordAccount(): boolean {
        if (this.providerIds?.find((providerId) => providerId === 'password')) {
            return true;
        }
        return false;
    }

    /**
     * The account signs in with Google, Apple, ... (code auth P2, task 4): the profile shows the "external provider"
     * card then. An account created per code has no provider at all (`providerData` empty), a password account the
     * `password` provider - both show the "e-mail address" card.
     */
    public get hasSocialProvider(): boolean {
        return (this.providerIds ?? []).some(providerId => SOCIAL_PROVIDER_IDS.includes(providerId));
    }

    public passwordHasBeenVerified(): boolean {
        if (!this.hasPasswordAccount) {
            return true;
        }
        return this.afAuthUser?.emailVerified;
    }

    /**
     * The address of the signed-in user is verified (Firebase Auth `emailVerified`; provider accounts such as
     * Google come verified). The profile's email settings card shows the switches only then (email settings
     * P3, E4) - the endpoint demands `email_verified` in the token anyway. False without a user.
     */
    public get emailVerified(): boolean {
        return this.afAuthUser?.emailVerified === true;
    }
}
